import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import { TbCirclePlus, TbHistory, TbMessages, TbSettings, TbSparkles } from 'react-icons/tb';
import { Box, Collapse, Flex, Icon, Menu, MenuItem, Tag, Text } from 'core/components';
import SudoTag from 'app/components/SudoTag';
import { getKentikAiSettingsNavItems } from 'app/views/settings/ai/kentikAiSettingsTabs';
import KaizenList from './KaizenList';
import { KaizenSidebar as StyledKaizenSidebar } from './KaizenChatSession.styles';
import { ResizeHandle } from './ResizablePanels';

const RECENT_CHAT_LIMIT = 10;

const SIDEBAR_MIN_WIDTH = 220;
const SIDEBAR_MAX_WIDTH = 420;
const SIDEBAR_KEYBOARD_STEP = 20;
const SIDEBAR_WIDTH_STORAGE_KEY = 'kaizenSidebarWidth';

const SESSIONS_SECTION_ID = 'kaizen-sidebar-sessions';
const RECENT_SECTION_ID = 'kaizen-sidebar-recent';
const SETTINGS_SECTION_ID = 'kaizen-sidebar-settings';

// Each AI Advisor page mounts its own sidebar, so remember which sections are open when moving between them
const sectionOpenState = { isSessionsOpen: true, isRecentOpen: true, isSettingsOpen: true };

const clampWidth = (width) => Math.round(Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width)));

const getStoredWidth = () => {
  const width = parseInt(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY), 10);
  return Number.isNaN(width) ? null : clampWidth(width);
};

const SidebarMenuItem = styled(MenuItem)`
  &.bp4-menu-item {
    padding: 5px 8px;
    align-items: center;
  }
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
`;

// matches the Explorer sidebar's section headers (see explorer/sidebar/SidebarSection)
const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 49px;
  margin: 0;
  padding: 0 16px;
  border: 0;
  border-bottom: ${(props) => (props.$isOpen ? '1px solid transparent' : props.theme.borders.thin)};
  border-radius: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.theme.name === 'dark' ? 'rgba(138,155,168,0.15)' : 'rgba(16,22,26,0.05)')};
  }
`;

const RecentChatsSkeleton = () => (
  <Flex flexDirection="column" gap="10px" px="8px" py="6px" aria-busy="true" aria-label="Loading recent chats">
    {[80, 60, 70, 50, 65].map((width) => (
      <Box key={width} className={Classes.SKELETON} height={12} width={`${width}%`} borderRadius={2} />
    ))}
  </Flex>
);

const SidebarSection = ({ id, title, icon, isOpen, onToggle, children }) => (
  <Flex as="section" flexDirection="column" aria-labelledby={`${id}-heading`}>
    <SectionHeading id={`${id}-heading`}>
      <SectionToggle type="button" $isOpen={isOpen} aria-expanded={isOpen} aria-controls={id} onClick={onToggle}>
        <Flex alignItems="center" gap="8px">
          <Icon iconSize={16} icon={icon} color="warning" />
          <Text as="div" fontWeight="bold">
            {title}
          </Text>
        </Flex>
        <Icon color="muted" icon={isOpen ? 'chevron-down' : 'chevron-right'} />
      </SectionToggle>
    </SectionHeading>
    <div id={id}>
      <Collapse isOpen={isOpen}>
        {/* menu item padding brings their icons in line with the header icon */}
        <Box p="8px" borderBottom="thin">
          {children}
        </Box>
      </Collapse>
    </div>
  </Flex>
);

@inject('$auth', '$kaizen')
@withRouter
@observer
class KaizenSidebar extends Component {
  state = { ...sectionOpenState, width: getStoredWidth(), renderedWidth: null };

  sidebarRef = React.createRef();

  componentDidMount() {
    const { $kaizen } = this.props;
    const { chatSessionCollection } = $kaizen;

    // the sidebar can be rendered outside of KaizenMain (e.g. History), so make sure recent chats are available
    if (!chatSessionCollection.hasFetched && !chatSessionCollection.loading) {
      chatSessionCollection.fetch();
    }

    // track the rendered width for the resize handle, since the CSS width follows the viewport
    if (typeof ResizeObserver !== 'undefined' && this.sidebarRef.current) {
      this.resizeObserver = new ResizeObserver(([entry]) => {
        this.setState({ renderedWidth: Math.round(entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width) });
      });
      this.resizeObserver.observe(this.sidebarRef.current);
    }
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
    this.stopDragging();
  }

  get historyUrl() {
    const { $kaizen } = this.props;
    return `${$kaizen.baseUrl}/history`;
  }

  get isHistoryActive() {
    const { location } = this.props;
    return location?.pathname === this.historyUrl;
  }

  handleToggleSessions = () => this.toggleSection('isSessionsOpen');

  handleToggleRecent = () => this.toggleSection('isRecentOpen');

  handleToggleSettings = () => this.toggleSection('isSettingsOpen');

  toggleSection(key) {
    sectionOpenState[key] = !sectionOpenState[key];
    this.setState({ [key]: sectionOpenState[key] });
  }

  // until the user resizes it, the sidebar's width comes from CSS
  get currentWidth() {
    const { width, renderedWidth } = this.state;
    return width ?? renderedWidth ?? this.sidebarRef.current?.offsetWidth ?? SIDEBAR_MIN_WIDTH;
  }

  setWidth(width) {
    const clampedWidth = clampWidth(width);
    this.setState({ width: clampedWidth });
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, clampedWidth);
  }

  handleResizeStart = (e) => {
    e.preventDefault();
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.stopDragging);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  handleResizeMove = (e) => {
    const sidebar = this.sidebarRef.current;
    if (sidebar) {
      this.setWidth(sidebar.getBoundingClientRect().right - e.clientX);
    }
  };

  stopDragging = () => {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.stopDragging);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  handleResizeKeyDown = (e) => {
    const widths = {
      // the handle is on the sidebar's left edge, so moving it left widens the sidebar
      ArrowLeft: this.currentWidth + SIDEBAR_KEYBOARD_STEP,
      ArrowRight: this.currentWidth - SIDEBAR_KEYBOARD_STEP,
      Home: SIDEBAR_MIN_WIDTH,
      End: SIDEBAR_MAX_WIDTH
    };

    if (e.key in widths) {
      e.preventDefault();
      this.setWidth(widths[e.key]);
    }
  };

  handleNewChat = () => {
    const { history, $kaizen, onNewChat } = this.props;
    if (onNewChat) {
      onNewChat();
    } else {
      history.push($kaizen.baseUrl);
    }
  };

  handleNavigate = (url) => {
    const { history } = this.props;
    history.push(url);
  };

  handleSelectJourney = (journey) => {
    const { history, $kaizen, onSelectJourney } = this.props;
    if (onSelectJourney) {
      onSelectJourney(journey);
    } else {
      history.push(`${$kaizen.baseUrl}/${journey.id}`);
    }
  };

  handleViewPrompt = () => {
    window.open('/api/ui/kaizen/context', '_blank');
  };

  render() {
    const { $auth, $kaizen, location } = this.props;
    const { isSessionsOpen, isRecentOpen, isSettingsOpen, width } = this.state;
    const { isSudoer } = $auth;
    const settingsItems = getKentikAiSettingsNavItems($auth, $kaizen.settingsBaseUrl);
    const { chatSessionCollection } = $kaizen;
    const recentChats = chatSessionCollection.userChats.slice(0, RECENT_CHAT_LIMIT);
    // the list loads in the background; only show a placeholder while there's nothing to show yet
    const isLoadingRecent = chatSessionCollection.loading && recentChats.length === 0;

    return (
      <Flex height="100%" flexShrink={0}>
        <ResizeHandle
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-controls="kaizen-sidebar"
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          aria-valuenow={this.currentWidth}
          tabIndex={0}
          onMouseDown={this.handleResizeStart}
          onKeyDown={this.handleResizeKeyDown}
        />
        <StyledKaizenSidebar id="kaizen-sidebar" ref={this.sidebarRef} style={width ? { width } : undefined}>
          <Flex flexDirection="column">
            <SidebarSection
              id={SESSIONS_SECTION_ID}
              title="AI Advisor Sessions"
              icon={TbSparkles}
              isOpen={isSessionsOpen}
              onToggle={this.handleToggleSessions}
            >
              <Menu p={0}>
                <SidebarMenuItem icon={TbCirclePlus} text="New" onClick={this.handleNewChat} />
                <SidebarMenuItem
                  icon={TbHistory}
                  text="History"
                  active={this.isHistoryActive}
                  onClick={() => this.handleNavigate(this.historyUrl)}
                />
              </Menu>
            </SidebarSection>

            <SidebarSection
              id={RECENT_SECTION_ID}
              title="Recent"
              icon={TbMessages}
              isOpen={isRecentOpen}
              onToggle={this.handleToggleRecent}
            >
              {isLoadingRecent ? (
                <RecentChatsSkeleton />
              ) : (
                <KaizenList
                  dense
                  hideUserName
                  hideShareLevel
                  journeys={recentChats}
                  onSelectJourney={this.handleSelectJourney}
                  emptyText="No chats yet."
                />
              )}
            </SidebarSection>

            {(settingsItems.length > 0 || isSudoer) && (
              <SidebarSection
                id={SETTINGS_SECTION_ID}
                title="AI Settings"
                icon={TbSettings}
                isOpen={isSettingsOpen}
                onToggle={this.handleToggleSettings}
              >
                <Menu p={0}>
                  {settingsItems.map((item) => (
                    <SidebarMenuItem
                      key={item.id}
                      icon={item.icon}
                      text={item.title}
                      labelElement={item.tagContent && <Tag {...item.tagProps}>{item.tagContent}</Tag>}
                      active={location?.pathname === item.url || location?.pathname?.startsWith(`${item.url}/`)}
                      onClick={() => this.handleNavigate(item.url)}
                    />
                  ))}
                  {isSudoer && (
                    <SidebarMenuItem
                      icon="document"
                      text={
                        <>
                          <SudoTag fontSize={11} /> View system prompt
                        </>
                      }
                      onClick={this.handleViewPrompt}
                    />
                  )}
                </Menu>
              </SidebarSection>
            )}
          </Flex>
        </StyledKaizenSidebar>
      </Flex>
    );
  }
}

export default KaizenSidebar;
